import { useWrapRequest } from 'use-wrap-request';
import { useNavigate } from 'react-router';
import { FieldState, FormState } from 'formstate';
import { Api } from '@core/api';
import { required, useModel, wrapInFormField } from '@core/forms';
import { injectTSDI } from '@core/tsdi';
import { links } from '@core/router';
import {
    CommunityCreateRequestSupportDto,
    CommunityType,
    CommunityAccessLevel,
    ChatNotificationSelection,
    I18NText,
    CdnObjectDto,
    ImageType,
    LocaleCode
} from '@api/ChallengeClient';
import { useState } from 'react';

export function useCreateCommunityStore() {
    const { challengeClient } = injectTSDI(Api);
    const navigate = useNavigate();

    // Image upload state
    const [imageUploadLoading, setImageUploadLoading] = useState(false);
    const [imageUploadError, setImageUploadError] = useState<
        string | undefined
    >();
    const [imagePreviewUrl, setImagePreviewUrl] = useState<
        string | undefined
    >();

    const createRequest = useWrapRequest(
        async (data: CommunityCreateRequestSupportDto) => {
            const result =
                await challengeClient.support.communitySupportControllerCreate(
                    data
                );
            return result;
        },
        {
            successMessage: ([data]) =>
                `Community "${data.name}" was created successfully`
        }
    );

    const model = useModel(() => {
        const form = new FormState({
            name: new FieldState<string>('').validators(required()),
            type: new FieldState<CommunityType>(
                CommunityType.STANDARD
            ).validators(required()),
            tenantId: new FieldState<string | undefined>(undefined),
            description: new FormState({
                en: new FieldState<string>(''),
                de: new FieldState<string>(''),
                pl: new FieldState<string>('')
            }),
            photo: new FormState({
                bucketName: new FieldState<string>('').validators(required()),
                objectKey: new FieldState<string>('').validators(required())
            }),
            managerUserIds: new FieldState<string>(''),
            memberInvitation: new FieldState<CommunityAccessLevel>(
                CommunityAccessLevel.ALL
            ).validators(required()),
            eventCreation: new FieldState<CommunityAccessLevel>(
                CommunityAccessLevel.ALL
            ).validators(required()),
            notificationsChat: new FieldState<ChatNotificationSelection>(
                ChatNotificationSelection.ALL
            ).validators(required()),
            tenantMemberAutoJoinEnabled: new FieldState<boolean>(false),
            leaveEnabled: new FieldState<boolean>(true),
            initMembers: new FieldState<boolean>(true),
            locale: new FieldState<LocaleCode>(LocaleCode.EnUS).validators(
                required()
            ),
        });

        // Manually wrap each field following the tenant onboarding pattern
        const name = wrapInFormField(form.$.name);
        const type = wrapInFormField(form.$.type);
        const tenantId = wrapInFormField(form.$.tenantId);
        const managerUserIds = wrapInFormField(form.$.managerUserIds);
        const memberInvitation = wrapInFormField(form.$.memberInvitation);
        const eventCreation = wrapInFormField(form.$.eventCreation);
        const notificationsChat = wrapInFormField(form.$.notificationsChat);
        const tenantMemberAutoJoinEnabled = wrapInFormField(
            form.$.tenantMemberAutoJoinEnabled
        );
        const leaveEnabled = wrapInFormField(form.$.leaveEnabled);
        const initMembers = wrapInFormField(form.$.initMembers);
        const locale = wrapInFormField(form.$.locale);
        const description = {
            en: wrapInFormField(form.$.description.$.en),
            de: wrapInFormField(form.$.description.$.de),
            pl: wrapInFormField(form.$.description.$.pl)
        };

        const photo = {
            bucketName: wrapInFormField(form.$.photo.$.bucketName),
            objectKey: wrapInFormField(form.$.photo.$.objectKey)
        };

        return {
            form,
            fields: {
                name,
                type,
                tenantId,
                description,
                photo,
                managerUserIds,
                memberInvitation,
                eventCreation,
                notificationsChat,
                tenantMemberAutoJoinEnabled,
                leaveEnabled,
                initMembers,
                locale
            }
        };
    });

    const handleImageUpload = async (file: File) => {
        setImageUploadLoading(true);
        setImageUploadError(undefined);

        try {
            const result =
                await challengeClient.support.imageSupportControllerUpload(
                    { type: ImageType.COMMUNITY },
                    { file }
                );

            // Update the form fields with the uploaded image data
            model.fields.photo.bucketName.onChange(result.data.bucketName);
            model.fields.photo.objectKey.onChange(result.data.objectKey);

            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreviewUrl(previewUrl);
        } catch (error) {
            console.error('Image upload failed:', error);
            setImageUploadError('Failed to upload image. Please try again.');
        } finally {
            setImageUploadLoading(false);
        }
    };

    const onSubmitHandler = async () => {
        const result = await model.form.validate();
        if (result.hasError) {
            return;
        }

        const { fields } = model;

        // Parse manager user IDs from comma-separated string
        const managerUserIds = fields.managerUserIds.value
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id.length > 0);

        // Build description object
        const description: I18NText = {};
        if (fields.description.en.value.trim()) {
            description.en = fields.description.en.value.trim();
        }
        if (fields.description.de.value.trim()) {
            description.de = fields.description.de.value.trim();
        }
        if (fields.description.pl.value.trim()) {
            description.pl = fields.description.pl.value.trim();
        }

        // Build photo object
        const photo: CdnObjectDto = {
            bucketName: fields.photo.bucketName.value,
            objectKey: fields.photo.objectKey.value
        };

        const communityData: CommunityCreateRequestSupportDto = {
            name: fields.name.value,
            type: fields.type.value,
            tenantId: fields.tenantId.value,
            description:
                Object.keys(description).length > 0 ? description : undefined,
            photo,
            managerUserIds,
            memberInvitation: fields.memberInvitation.value,
            eventCreation: fields.eventCreation.value,
            tenantMemberAutoJoinEnabled:
                fields.tenantMemberAutoJoinEnabled.value,
            leaveEnabled: fields.leaveEnabled.value,
            initMembers: fields.initMembers.value,
            notificationsChat: fields.notificationsChat.value,
            locale: fields.locale.value
        };

        const response = await createRequest.request([communityData]);
        if (response) {
            navigate(links.communities());
        }
    };

    const onCancelHandler = () => {
        navigate(links.communities());
    };

    return {
        fields: model.fields,
        onSubmitHandler,
        createRequest,
        onCancelHandler,
        imageUpload: {
            loading: imageUploadLoading,
            error: imageUploadError,
            previewUrl: imagePreviewUrl,
            handleUpload: handleImageUpload
        }
    };
}
